// Live OAuth Testing Service
// Validates real OAuth tokens and tests API calls with rate limit monitoring

import { createClient } from '@blinkdotnew/sdk';
import { getValidAccessToken } from './oauthService';
import { safeJsonParse } from '@/lib/utils';

const blink = createClient({
  projectId: 'lemon-ui-agentic-email-ai-agent-ben3ou87',
  authRequired: false
});

export interface RateLimitInfo {
  provider: 'gmail' | 'outlook';
  endpoint: string;
  limit: number;
  remaining: number;
  reset: Date;
  retryAfter?: number;
}

export interface TokenValidation {
  provider: 'gmail' | 'outlook';
  isValid: boolean;
  expiresAt?: Date;
  scopes?: string[];
  email?: string;
  error?: string;
}

export interface LiveTestResult {
  id: string;
  timestamp: Date;
  provider: 'gmail' | 'outlook';
  operation: string;
  method: string;
  endpoint: string;
  status: 'success' | 'error' | 'rate_limited';
  statusCode?: number;
  duration: number;
  requestSize?: number;
  responseSize?: number;
  rateLimit?: RateLimitInfo;
  request: any;
  response?: any;
  error?: string;
}

class LiveOAuthTestService {
  private testResults: LiveTestResult[] = [];
  private rateLimits: Map<string, RateLimitInfo> = new Map();

  /**
   * Validate OAuth token with provider
   */
  async validateToken(userId: string, provider: 'gmail' | 'outlook'): Promise<TokenValidation> {
    console.log(`🔍 Validating ${provider} token for user ${userId}...`);

    try {
      // Get access token
      const accessToken = await getValidAccessToken(provider, userId);
      
      if (!accessToken) {
        return {
          provider,
          isValid: false,
          error: 'No access token available'
        };
      }

      // Validate token with provider
      if (provider === 'gmail') {
        return await this.validateGmailToken(accessToken);
      } else {
        return await this.validateOutlookToken(accessToken);
      }
    } catch (error) {
      console.error('Token validation error:', error);
      return {
        provider,
        isValid: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  /**
   * Validate Gmail token using Google's tokeninfo endpoint
   */
  private async validateGmailToken(accessToken: string): Promise<TokenValidation> {
    try {
      const response = await fetch(`https://www.googleapis.com/oauth2/v1/tokeninfo?access_token=${accessToken}`);
      
      if (!response.ok) {
        return {
          provider: 'gmail',
          isValid: false,
          error: 'Token validation failed'
        };
      }

      const data = await response.json();
      
      return {
        provider: 'gmail',
        isValid: true,
        expiresAt: new Date(Date.now() + (data.expires_in * 1000)),
        scopes: data.scope?.split(' ') || [],
        email: data.email
      };
    } catch (error) {
      return {
        provider: 'gmail',
        isValid: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  /**
   * Validate Outlook token using Microsoft Graph's /me endpoint
   */
  private async validateOutlookToken(accessToken: string): Promise<TokenValidation> {
    try {
      const response = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      
      if (!response.ok) {
        return {
          provider: 'outlook',
          isValid: false,
          error: 'Token validation failed'
        };
      }

      const data = await response.json();
      
      return {
        provider: 'outlook',
        isValid: true,
        email: data.mail || data.userPrincipalName
      };
    } catch (error) {
      return {
        provider: 'outlook',
        isValid: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  /**
   * Extract rate limit info from response headers
   */
  private extractRateLimit(provider: 'gmail' | 'outlook', endpoint: string, headers: Headers): RateLimitInfo | undefined {
    if (provider === 'gmail') {
      const limit = headers.get('X-RateLimit-Limit');
      const remaining = headers.get('X-RateLimit-Remaining');
      const reset = headers.get('X-RateLimit-Reset');

      if (limit && remaining && reset) {
        const info: RateLimitInfo = {
          provider,
          endpoint,
          limit: parseInt(limit),
          remaining: parseInt(remaining),
          reset: new Date(parseInt(reset) * 1000)
        };
        
        this.rateLimits.set(`${provider}:${endpoint}`, info);
        return info;
      }
    } else {
      // Outlook uses different headers
      const limit = headers.get('X-MS-Resource-Unit-Quota');
      const remaining = headers.get('X-MS-Resource-Unit-Remaining');
      const retryAfter = headers.get('Retry-After');

      if (limit && remaining) {
        const info: RateLimitInfo = {
          provider,
          endpoint,
          limit: parseInt(limit),
          remaining: parseInt(remaining),
          reset: new Date(Date.now() + 60000), // Outlook resets every minute
          retryAfter: retryAfter ? parseInt(retryAfter) : undefined
        };
        
        this.rateLimits.set(`${provider}:${endpoint}`, info);
        return info;
      }
    }

    return undefined;
  }

  /**
   * Make a live API call with monitoring
   */
  async makeLiveAPICall(
    userId: string,
    provider: 'gmail' | 'outlook',
    operation: string,
    method: string,
    endpoint: string,
    options?: {
      body?: any;
      queryParams?: Record<string, string>;
      expectedStatus?: number;
    }
  ): Promise<LiveTestResult> {
    const testId = `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const startTime = Date.now();

    const result: LiveTestResult = {
      id: testId,
      timestamp: new Date(),
      provider,
      operation,
      method,
      endpoint,
      status: 'error',
      duration: 0,
      request: {
        method,
        endpoint,
        body: options?.body,
        queryParams: options?.queryParams
      }
    };

    try {
      // Get access token
      const accessToken = await getValidAccessToken(provider, userId);
      
      if (!accessToken) {
        throw new Error(`No valid access token for ${provider}`);
      }

      // Build URL with query params
      const url = new URL(endpoint);
      if (options?.queryParams) {
        Object.entries(options.queryParams).forEach(([key, value]) => {
          url.searchParams.set(key, value);
        });
      }

      // Make request
      const requestOptions: RequestInit = {
        method,
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      };

      if (options?.body) {
        requestOptions.body = JSON.stringify(options.body);
        result.requestSize = JSON.stringify(options.body).length;
      }

      console.log(`📡 Making live ${method} request to ${url.toString()}`);
      
      const response = await fetch(url.toString(), requestOptions);
      
      result.duration = Date.now() - startTime;
      result.statusCode = response.status;

      // Extract rate limit info
      result.rateLimit = this.extractRateLimit(provider, endpoint, response.headers);

      // Check for rate limiting
      if (response.status === 429) {
        result.status = 'rate_limited';
        const retryAfter = response.headers.get('Retry-After');
        result.error = `Rate limited. Retry after ${retryAfter || 'unknown'} seconds`;
        
        // Update rate limit info
        if (result.rateLimit) {
          result.rateLimit.retryAfter = retryAfter ? parseInt(retryAfter) : undefined;
        }
        
        this.testResults.push(result);
        return result;
      }

      // Handle response
      if (response.ok || response.status === (options?.expectedStatus || 200)) {
        const responseText = await response.text();
        result.responseSize = responseText.length;
        
        try {
          result.response = safeJsonParse(responseText);
        } catch {
          result.response = responseText;
        }
        
        result.status = 'success';
        console.log(`✅ ${operation} successful (${result.duration}ms)`);
      } else {
        const errorText = await response.text();
        let errorData;
        
        try {
          errorData = safeJsonParse(errorText);
          result.error = errorData.error?.message || errorText;
        } catch {
          result.error = errorText;
        }
        
        result.status = 'error';
        console.error(`❌ ${operation} failed:`, result.error);
      }

    } catch (error) {
      result.duration = Date.now() - startTime;
      result.error = error instanceof Error ? error.message : String(error);
      result.status = 'error';
      console.error(`❌ ${operation} error:`, error);
    }

    this.testResults.push(result);
    return result;
  }

  /**
   * Gmail API Tests
   */
  async testGmailSendEmail(userId: string, to: string, subject: string, body: string): Promise<LiveTestResult> {
    return this.makeLiveAPICall(
      userId,
      'gmail',
      'Send Email',
      'POST',
      'https://gmail.googleapis.com/gmail/v1/users/me/messages/send',
      {
        body: {
          raw: btoa(
            `To: ${to}\r\n` +
            `Subject: ${subject}\r\n` +
            `Content-Type: text/plain; charset=utf-8\r\n\r\n` +
            body
          ).replace(/\+/g, '-').replace(/\//g, '_')
        }
      }
    );
  }

  async testGmailListMessages(userId: string, maxResults: number = 10): Promise<LiveTestResult> {
    return this.makeLiveAPICall(
      userId,
      'gmail',
      'List Messages',
      'GET',
      'https://gmail.googleapis.com/gmail/v1/users/me/messages',
      {
        queryParams: {
          maxResults: maxResults.toString()
        }
      }
    );
  }

  async testGmailSearchMessages(userId: string, query: string): Promise<LiveTestResult> {
    return this.makeLiveAPICall(
      userId,
      'gmail',
      'Search Messages',
      'GET',
      'https://gmail.googleapis.com/gmail/v1/users/me/messages',
      {
        queryParams: {
          q: query,
          maxResults: '10'
        }
      }
    );
  }

  async testGmailGetMessage(userId: string, messageId: string): Promise<LiveTestResult> {
    return this.makeLiveAPICall(
      userId,
      'gmail',
      'Get Message',
      'GET',
      `https://gmail.googleapis.com/gmail/v1/users/me/messages/${messageId}`
    );
  }

  /**
   * Outlook API Tests
   */
  async testOutlookSendEmail(
    userId: string,
    to: string,
    subject: string,
    body: string
  ): Promise<LiveTestResult> {
    return this.makeLiveAPICall(
      userId,
      'outlook',
      'Send Email',
      'POST',
      'https://graph.microsoft.com/v1.0/me/sendMail',
      {
        body: {
          message: {
            subject,
            body: { contentType: 'Text', content: body },
            toRecipients: [{ emailAddress: { address: to } }]
          }
        },
        expectedStatus: 202 // Outlook returns 202 Accepted for sendMail
      }
    );
  }

  async testOutlookListMessages(userId: string, top: number = 10): Promise<LiveTestResult> {
    return this.makeLiveAPICall(
      userId,
      'outlook',
      'List Messages',
      'GET',
      'https://graph.microsoft.com/v1.0/me/messages',
      {
        queryParams: {
          $top: top.toString(),
          $select: 'subject,from,receivedDateTime,bodyPreview'
        }
      }
    );
  }

  async testOutlookSearchMessages(userId: string, query: string): Promise<LiveTestResult> {
    return this.makeLiveAPICall(
      userId,
      'outlook',
      'Search Messages',
      'GET',
      'https://graph.microsoft.com/v1.0/me/messages',
      {
        queryParams: {
          $search: `"${query}"`,
          $top: '10',
          $select: 'subject,from,receivedDateTime,bodyPreview'
        }
      }
    );
  }

  async testOutlookCreateCalendarEvent(
    userId: string,
    subject: string,
    start: string,
    end: string,
    attendees: string[]
  ): Promise<LiveTestResult> {
    return this.makeLiveAPICall(
      userId,
      'outlook',
      'Create Calendar Event',
      'POST',
      'https://graph.microsoft.com/v1.0/me/events',
      {
        body: {
          subject,
          start: { dateTime: start, timeZone: 'UTC' },
          end: { dateTime: end, timeZone: 'UTC' },
          attendees: attendees.map(email => ({
            emailAddress: { address: email },
            type: 'required'
          }))
        }
      }
    );
  }

  async testOutlookListCalendarEvents(userId: string, top: number = 10): Promise<LiveTestResult> {
    const now = new Date().toISOString();
    const nextWeek = new Date(Date.now() + 7 * 86400000).toISOString();
    
    return this.makeLiveAPICall(
      userId,
      'outlook',
      'List Calendar Events',
      'GET',
      'https://graph.microsoft.com/v1.0/me/calendarView',
      {
        queryParams: {
          startDateTime: now,
          endDateTime: nextWeek,
          $select: 'subject,start,end,location,organizer',
          $orderby: 'start/dateTime',
          $top: top.toString()
        }
      }
    );
  }

  /**
   * Run comprehensive test suite
   */
  async runComprehensiveTest(
    userId: string,
    provider: 'gmail' | 'outlook',
    options: {
      testEmail?: string;
      testSubject?: string;
      testBody?: string;
      skipSend?: boolean;
    } = {}
  ): Promise<LiveTestResult[]> {
    const results: LiveTestResult[] = [];

    console.log(`\n🧪 Starting comprehensive ${provider} API test suite...`);

    // 1. Validate token
    console.log('\n1️⃣ Validating OAuth token...');
    const tokenValidation = await this.validateToken(userId, provider);
    console.log('Token validation result:', tokenValidation);

    if (!tokenValidation.isValid) {
      throw new Error(`Invalid ${provider} token: ${tokenValidation.error}`);
    }

    if (provider === 'gmail') {
      // Gmail tests
      console.log('\n2️⃣ Testing Gmail list messages...');
      results.push(await this.testGmailListMessages(userId, 10));

      console.log('\n3️⃣ Testing Gmail search...');
      results.push(await this.testGmailSearchMessages(userId, 'is:inbox'));

      if (!options.skipSend && options.testEmail) {
        console.log('\n4️⃣ Testing Gmail send email...');
        results.push(await this.testGmailSendEmail(
          userId,
          options.testEmail,
          options.testSubject || 'Live API Test',
          options.testBody || 'This is a live test from the OAuth validation service.'
        ));
      }

    } else {
      // Outlook tests
      console.log('\n2️⃣ Testing Outlook list messages...');
      results.push(await this.testOutlookListMessages(userId, 10));

      console.log('\n3️⃣ Testing Outlook search...');
      results.push(await this.testOutlookSearchMessages(userId, 'inbox'));

      console.log('\n4️⃣ Testing Outlook list calendar events...');
      results.push(await this.testOutlookListCalendarEvents(userId, 10));

      if (!options.skipSend && options.testEmail) {
        console.log('\n5️⃣ Testing Outlook send email...');
        results.push(await this.testOutlookSendEmail(
          userId,
          options.testEmail,
          options.testSubject || 'Live API Test',
          options.testBody || 'This is a live test from the OAuth validation service.'
        ));
      }
    }

    console.log(`\n✅ Comprehensive test completed: ${results.filter(r => r.status === 'success').length}/${results.length} passed`);

    return results;
  }

  /**
   * Get current rate limit status
   */
  getRateLimitStatus(provider: 'gmail' | 'outlook'): RateLimitInfo[] {
    const limits: RateLimitInfo[] = [];
    
    this.rateLimits.forEach((info, key) => {
      if (key.startsWith(`${provider}:`)) {
        limits.push(info);
      }
    });

    return limits;
  }

  /**
   * Get test results
   */
  getTestResults(options?: {
    provider?: 'gmail' | 'outlook';
    status?: 'success' | 'error' | 'rate_limited';
    limit?: number;
  }): LiveTestResult[] {
    let results = [...this.testResults];

    if (options?.provider) {
      results = results.filter(r => r.provider === options.provider);
    }

    if (options?.status) {
      results = results.filter(r => r.status === options.status);
    }

    if (options?.limit) {
      results = results.slice(0, options.limit);
    }

    return results.reverse(); // Most recent first
  }

  /**
   * Clear test results
   */
  clearResults(): void {
    this.testResults = [];
    this.rateLimits.clear();
  }

  /**
   * Get test statistics
   */
  getStatistics(): {
    total: number;
    success: number;
    error: number;
    rateLimited: number;
    avgDuration: number;
    byProvider: {
      gmail: { total: number; success: number; error: number };
      outlook: { total: number; success: number; error: number };
    };
  } {
    const stats = {
      total: this.testResults.length,
      success: this.testResults.filter(r => r.status === 'success').length,
      error: this.testResults.filter(r => r.status === 'error').length,
      rateLimited: this.testResults.filter(r => r.status === 'rate_limited').length,
      avgDuration: this.testResults.reduce((sum, r) => sum + r.duration, 0) / this.testResults.length || 0,
      byProvider: {
        gmail: {
          total: this.testResults.filter(r => r.provider === 'gmail').length,
          success: this.testResults.filter(r => r.provider === 'gmail' && r.status === 'success').length,
          error: this.testResults.filter(r => r.provider === 'gmail' && r.status === 'error').length
        },
        outlook: {
          total: this.testResults.filter(r => r.provider === 'outlook').length,
          success: this.testResults.filter(r => r.provider === 'outlook' && r.status === 'success').length,
          error: this.testResults.filter(r => r.provider === 'outlook' && r.status === 'error').length
        }
      }
    };

    return stats;
  }
}

export const liveOAuthTestService = new LiveOAuthTestService();
